import SEO from '../../components/SEO';
import React from 'react';
import { Stethoscope } from 'lucide-react';
import FAQRichSnippet from '../../components/FAQRichSnippet';

import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';

const ASSESSMENT_FAQS = [
  { q: "What is a comprehensive psychiatric assessment?", a: "A comprehensive assessment is a thorough clinical evaluation that examines your emotional, behavioral, and mental health needs. It includes reviewing your history, current symptoms, functioning level, and goals to create an accurate diagnosis and personalized treatment recommendations." },
  { q: "How long does the assessment take?", a: "A comprehensive assessment typically takes 60 to 90 minutes. Your clinician will explain the process beforehand so you know what to expect." },
  { q: "Do I need a referral for an assessment?", a: "No referral is required. You can schedule an assessment directly by calling (877) 772-5150 or submitting an intake form online." },
  { q: "What happens after the assessment?", a: "After the assessment, your clinician will discuss findings with you and recommend an individualized treatment plan. This may include therapy, medication management, PRP services, or a combination based on your needs." },
];

const ComprehensiveAssessmentPage = () => {
  return (
    <div className="min-h-screen bg-white">
      
      <Navigation />
      <SEO title="Comprehensive Psychiatric Assessment" description="Comprehensive psychiatric assessments at SDORA Wellbeing: a thorough 60-90 minute evaluation of your emotional, behavioral, and mental health needs to guide diagnosis and personalized treatment planning in Pikesville, MD." image="https://static.prod-images.emergentagent.com/jobs/01501ca6-ddde-43b6-bfc5-0444b2f0bae0/images/408acebefad30991ecdb006ab570a8b555f17eaec82f9db20f422394fd69e252.jpeg" path="/services/comprehensive-assessment" />
      
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Stethoscope className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Comprehensive Assessment
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl">
            Thorough evaluations to understand root causes and develop effective treatment strategies.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Assessment Process</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Initial Evaluation</h3>
                    <p className="text-gray-600">Comprehensive review of your mental health history and current concerns.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Diagnostic Clarity</h3>
                    <p className="text-gray-600">Accurate diagnosis using evidence-based assessment tools.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Treatment Planning</h3>
                    <p className="text-gray-600">Develop personalized treatment recommendations based on findings.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Ongoing Assessment</h3>
                    <p className="text-gray-600">Regular evaluations to monitor progress and adjust treatment.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-teal-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What's Included</h2>
              <p className="text-gray-700 mb-4">Our comprehensive assessment covers:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Mental health history review</li>
                <li>• Current symptoms assessment</li>
                <li>• Medical history evaluation</li>
                <li>• Social and family history</li>
                <li>• Risk assessment</li>
                <li>• Strengths identification</li>
                <li>• Treatment recommendations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQRichSnippet faqs={ASSESSMENT_FAQS} />
      </div>
      <Footer />
    </div>
  );
};

export default ComprehensiveAssessmentPage;